import { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight, ChevronLeft } from "lucide-react";

const SLIDES = [
  {
    eyebrow: "Enterprise Language Intelligence",
    title: "Language Intelligence. Global Reach.",
    sub: "Human expertise. AI-enhanced precision.",
    body: "Certified translation, localization, interpretation, and AI-powered language solutions across 120+ languages.",
    image:
      "https://customer-assets.emergentagent.com/job_localize-intelligent/artifacts/za31dypd_photo-1531266752426-aad472b7bbf4.avif",
    focal: "center 50%",
  },
  {
    eyebrow: "AI Quality, Verified by Humans",
    title: "Scale Multilingual AI Products.",
    sub: "Native linguists powering smarter models.",
    body: "MTPE, AI content review, QA, and multilingual data workflows for global product teams and AI labs.",
    image:
      "https://customer-assets.emergentagent.com/job_localize-intelligent/artifacts/02b5g8fu_projekti_renaissance_hero.png",
    focal: "center 55%",
  },
  {
    eyebrow: "Caribbean & Underserved Markets",
    title: "Rare Languages. Real Experts.",
    sub: "Specialized in Caribbean and underserved markets.",
    body: "Haitian Creole, African languages, and low-resource linguistic ecosystems handled with cultural precision.",
    image:
      "https://customer-assets.emergentagent.com/job_localize-intelligent/artifacts/z3icba4x_people.png",
    focal: "center 45%",
  },
];

const SLIDE_MS = 6000;       // strict 6s per slide
const FADE_OUT_MS = 400;     // wrapper fade-out window before swap
const TYPE_SPEED_MS = 55;    // ms per character for title typewriter

/** Typewriter hook: types `text` one char at a time when `runKey` changes.
 *  Returns {typed, done}. Resets cleanly on each runKey flip. */
function useTypewriter(text, runKey, speed = TYPE_SPEED_MS) {
  const [typed, setTyped] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    setTyped("");
    setDone(false);
    if (!text) {
      setDone(true);
      return undefined;
    }
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setTyped(text.slice(0, i));
      if (i >= text.length) {
        clearInterval(id);
        setDone(true);
      }
    }, speed);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runKey, speed]);

  return { typed, done };
}

export default function HeroCarousel() {
  const [idx, setIdx] = useState(0);
  const [textVisible, setTextVisible] = useState(true);
  const [paused, setPaused] = useState(false);
  const intervalRef = useRef(null);
  const fadeOutTimerRef = useRef(null);

  const slide = SLIDES[idx];
  // runKey changes on every slide swap → typewriter restarts from scratch
  const { typed: typedTitle, done: titleDone } = useTypewriter(slide.title, idx);

  /** Schedule the wrapper fade-out so it completes EXACTLY at the SLIDE_MS
   *  boundary, then commit the swap and reveal the next slide's text. */
  const scheduleCycle = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (fadeOutTimerRef.current) clearTimeout(fadeOutTimerRef.current);

    intervalRef.current = setInterval(() => {
      // Start fade-out FADE_OUT_MS before the swap so the old text is gone
      // by the time we change idx.
      setTextVisible(false);
      fadeOutTimerRef.current = setTimeout(() => {
        setIdx((prev) => (prev + 1) % SLIDES.length);
        setTextVisible(true);
      }, FADE_OUT_MS);
    }, SLIDE_MS);
  }, []);

  useEffect(() => {
    if (paused) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return undefined;
    }
    scheduleCycle();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (fadeOutTimerRef.current) clearTimeout(fadeOutTimerRef.current);
    };
  }, [paused, scheduleCycle]);

  /** Manual nav: swap immediately (with fade-out) and restart the 6s clock
   *  so the new slide gets a full 6s window. The new interval is scheduled
   *  AFTER the fade-out swap completes so it doesn't cancel our own timer. */
  const goTo = useCallback(
    (nextIdx) => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (fadeOutTimerRef.current) clearTimeout(fadeOutTimerRef.current);
      setTextVisible(false);
      fadeOutTimerRef.current = setTimeout(() => {
        const safe = ((nextIdx % SLIDES.length) + SLIDES.length) % SLIDES.length;
        setIdx(safe);
        setTextVisible(true);
        if (!paused) scheduleCycle();
      }, FADE_OUT_MS);
    },
    [paused, scheduleCycle]
  );

  const next = useCallback(() => goTo(idx + 1), [idx, goTo]);
  const prev = useCallback(() => goTo(idx - 1), [idx, goTo]);

  return (
    <section
      data-testid="hero-carousel"
      className="hero-carousel relative w-full text-white overflow-hidden bg-[#04132F]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div
        data-testid="hero-canvas"
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
      />

      {/* Backgrounds — crossfade between slides, zoom restarts on each idx change */}
      <div className="absolute inset-0">
        {SLIDES.map((s, i) => {
          const isActive = i === idx;
          return (
            <div
              key={s.image}
              data-testid={`hero-slide-bg-${i}`}
              className={`absolute inset-0 transition-opacity duration-500 ease-in-out ${
                isActive ? "opacity-100" : "opacity-0"
              }`}
              aria-hidden={!isActive}
            >
              {/* Inner div is keyed on idx so it remounts when this slide becomes
                  active and restarts the hero-zoom animation from scale(1). */}
              <div
                key={`bg-${i}-${idx}`}
                className="absolute inset-0 hero-zoom"
                style={{
                  backgroundImage: `url(${s.image})`,
                  backgroundSize: "cover",
                  backgroundPosition: s.focal,
                  backgroundRepeat: "no-repeat",
                }}
              />
            </div>
          );
        })}

        {/* Overlays */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#04132F]/85 via-[#071D49]/72 to-[#00D1B2]/18" />
        <div className="absolute inset-0 bg-[#04132F]/35" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#04132F] to-transparent" />
        <div className="absolute right-10 top-32 w-72 h-72 rounded-full bg-[#00D1B2]/12 blur-3xl pointer-events-none animate-float" />
      </div>

      {/* Content */}
      <div className="relative flex items-center" style={{ minHeight: "min(78vh, 720px)" }}>
        <div className="max-w-[1320px] mx-auto px-6 lg:px-10 w-full pt-16 lg:pt-20 pb-10">
          <div className="max-w-2xl">
            {/* Animated text block — eyebrow + headline + sub + body. Wrapper
                fades out 400ms before each swap so old text never overlaps
                new text. Inner element keyed on idx so CSS animations
                (fadeUp on sub/body) restart per slide. */}
            <div
              className={`hero-text-wrap transition-opacity duration-[400ms] ease-in-out ${
                textVisible ? "opacity-100" : "opacity-0"
              }`}
              data-testid={`hero-slide-${idx}`}
            >
              <div key={`text-${idx}`}>
                <p
                  className="eyebrow-teal hero-eyebrow-in"
                  data-testid="hero-eyebrow"
                >
                  {slide.eyebrow}
                </p>

                <h1
                  data-testid="hero-headline"
                  className="mt-3 text-3xl sm:text-4xl lg:text-[44px] xl:text-[52px] font-semibold leading-[1.06] tracking-tight text-white min-h-[1.2em]"
                  aria-label={slide.title}
                >
                  <span className="hero-type">{typedTitle}</span>
                  <span
                    className={`hero-caret${titleDone ? " hero-caret--done" : ""}`}
                    aria-hidden="true"
                  >
                    |
                  </span>
                </h1>

                <p
                  data-testid="hero-sub"
                  className="hero-sub-in mt-3 text-base lg:text-xl text-white font-light leading-snug"
                >
                  {slide.sub}
                </p>

                <p
                  data-testid="hero-desc"
                  className="hero-desc-in mt-2.5 text-[14px] lg:text-[15px] text-white/75 leading-relaxed max-w-xl"
                >
                  {slide.body}
                </p>
              </div>
            </div>

            {/* PERSISTENT CTAs — outside the animated wrapper. Never fade,
                never re-render with slide changes. */}
            <div
              className="mt-6 flex flex-wrap items-center gap-3"
              data-testid="hero-cta-row"
            >
              <Link
                to="/contact"
                data-testid="hero-cta-primary"
                className="btn-cta btn-primary"
              >
                Get a Quote
                <ArrowRight className="btn-arrow w-4 h-4" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-cta-secondary"
                className="btn-cta btn-ghost-white"
              >
                Explore Services
                <ChevronRight className="btn-arrow w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Side arrows */}
      <button
        onClick={prev}
        data-testid="hero-prev"
        aria-label="Previous slide"
        className="hidden md:flex absolute left-5 lg:left-8 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full items-center justify-center bg-white/8 hover:bg-white/15 border border-white/15 backdrop-blur transition-all z-10 group"
      >
        <ChevronLeft className="w-5 h-5 text-white group-hover:-translate-x-0.5 transition-transform" />
      </button>
      <button
        onClick={next}
        data-testid="hero-next"
        aria-label="Next slide"
        className="hidden md:flex absolute right-5 lg:right-8 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full items-center justify-center bg-white/8 hover:bg-white/15 border border-white/15 backdrop-blur transition-all z-10 group"
      >
        <ChevronRight className="w-5 h-5 text-white group-hover:translate-x-0.5 transition-transform" />
      </button>
    </section>
  );
}
